<script setup lang="ts">
import type { Component } from 'vue'
import BaseButton from '../Buttons/BaseButton.vue'
import { OctagonExclamationIcon } from '@/icons/'

interface Props {
  icon?: Component
  title?: string
  message?: string
  hint?: string
  showRetry?: boolean
  showTestLogin?: boolean
  retryText?: string
  testLoginText?: string
  onRetry?: () => void
  onTestLogin?: () => void
  variant?: 'error' | 'warning' | 'info'
}

const props = withDefaults(defineProps<Props>(), {
  icon: OctagonExclamationIcon,
  title: 'Something went wrong',
  message: '',
  hint: 'Make sure backend is running',
  showRetry: true,
  showTestLogin: false,
  retryText: 'Retry',
  testLoginText: 'Test Login',
  onRetry: undefined,
  onTestLogin: undefined,
  variant: 'error'
})

const emit = defineEmits<{
  retry: []
  testLogin: []
}>()

const variantClasses = {
  error: {
    icon: 'text-red-500',
    title: 'text-red-600',
    message: 'text-red-500',
    hint: 'text-red-400',
    button: 'bg-red-600 hover:bg-red-700 text-white',
    ghostButton: 'text-red-600 hover:text-red-800 border-red-200 hover:border-red-300'
  },
  warning: {
    icon: 'text-yellow-500',
    title: 'text-yellow-600',
    message: 'text-yellow-500',
    hint: 'text-yellow-400',
    button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    ghostButton: 'text-yellow-600 hover:text-yellow-800 border-yellow-200 hover:border-yellow-300'
  },
  info: {
    icon: 'text-blue-500',
    title: 'text-blue-600',
    message: 'text-blue-500',
    hint: 'text-blue-400',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
    ghostButton: 'text-blue-600 hover:text-blue-800 border-blue-200 hover:border-blue-300'
  }
}

const handleRetry = () => {
  if (props.onRetry) {
    props.onRetry()
  }
  emit('retry')
}

const handleTestLogin = () => {
  if (props.onTestLogin) {
    props.onTestLogin()
  }
  emit('testLogin')
}
</script>

<template>
  <div class="w-full flex flex-col items-center text-center py-8 gap-3">
    <component :is="icon" :class="['w-12 h-12', variantClasses[variant].icon]" />
    
    <div class="space-y-2">
      <p :class="['text-lg font-medium', variantClasses[variant].title]">
        {{ title }}
      </p>
      <p v-if="message" :class="['text-sm', variantClasses[variant].message]">
        {{ message }}
      </p>
      <p v-if="hint" :class="['text-xs', variantClasses[variant].hint]">
        {{ hint }}
      </p>
    </div>

    <div class="flex gap-2 mt-2">
      <BaseButton
        v-if="showRetry"
        :text="retryText"
        size="sm"
        :class="variantClasses[variant].button"
        @click="handleRetry"
      />
      <BaseButton
        v-if="showTestLogin"
        :text="testLoginText"
        size="sm"
        variant="ghost"
        :class="variantClasses[variant].ghostButton"
        @click="handleTestLogin"
      />
    </div>
  </div>
</template>


  <!-- Error State - Default (Red) -->
  <!-- <ErrorState
    v-if="error"
    :message="error"
    hint="Make sure backend is running"
    :show-test-login="true"
    @retry="fetchUsers"
    @test-login="testLogin"
  /> -->

  <!-- Warning State (Yellow) -->
  <!-- <ErrorState
    variant="warning"
    icon="OctagonInfoIcon"
    title="Warning"
    message="This action cannot be undone"
    hint="Please confirm before proceeding"
    :show-retry="false"
  /> -->

  <!-- Info State (Blue) -->
  <!-- <ErrorState
    variant="info"
    :icon="OctagonInfoIcon"
    title="Information"
    message="No data available"
    hint="Check back later"
    :show-retry="false"
  /> -->

  <!-- Custom Error State -->
  <!-- <ErrorState
    icon="OctagonCrossIcon"
    title="Custom Error"
    message="Something went wrong"
    hint="Please try again in a few minutes"
    retry-text="Try Again"
    @retry="handleRetry"
  /> -->